import React, {FC, RefObject, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownItemProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {DropdownItemMultiline} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemMultiline';
import {InteractiveListItem} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getChannelIconName} from '../../../../../helpers/channelViewHelpers';
import {getContactSourceFromMessageType} from '../../../../../helpers/modelHelpers';
import {ConversationBaseModel, RConversation} from '../../../../../models/api/conversationModel';
import {makeGetVisualConversationSenders} from '../../../../../store/data/conversations/conversationSendersSelectors';
import {getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {AsyncConversationPreviewer} from '../../../../common/conversations/asyncConversationPreviewer';
import {ConversationPreviewContainer} from '../../../../common/conversations/conversationPreviewContainer';
import {formatSenderName} from '../../../../common/formatters/senders/common';
import {placeholderSubjectsByMessageType} from '../../../cells/content/cell/cellMiddle';

/*
 * Constants.
 */

const dropdownItemHeight = 48; // px.

/*
 * Props.
 */

interface LinkConversationsDropdownRowProps extends DropdownItemProps {
  panelId: ViewerPanelId;
  onOriginalConversationSelect: (arg: number) => void;
  conversation: ConversationBaseModel;
}

/*
 * Styled.
 */

const StyledDropdownItem = styled(InteractiveListItem)`
  height: ${dropdownItemHeight}px;
`;

const StyledDropdownItemHeaderTexDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  line-height: 1.1;
  ${ellipsis()};
`;

const StyledDropdownItemSubHeaderTexDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  ${ellipsis()};
`;

const StyledContentDiv = styled.div`
  display: grid;
  grid-template-columns: 12px auto auto 1fr auto auto 12px;
  grid-template-areas: '. left-icon content secondary-content badges right-icon end-padding';
  justify-items: start;
  padding: 5px 0;
`;

interface LinkConversationsDropdownItemStyleProps {
  $isHighlighted: boolean;
}

export const StyledLinkConversationsItemDiv = styled.div<LinkConversationsDropdownItemStyleProps>`
  justify-items: start;

  ${(p) => maybeHighlightedStyle(p.$isHighlighted)}
`;

function maybeHighlightedStyle(isHighlighted: boolean) {
  if (!isHighlighted) {
    return '';
  }

  return css`
    background-color: ${(p) => p.theme.alphas.gray10};
  `;
}

/*
 * Component.
 */

export const LinkConversationsDropdownRow: FC<LinkConversationsDropdownRowProps> = (props) => {
  const {conversation} = props;
  if (conversation) {
    return <LinkConversationRow {...props} />;
  }

  return null;
};

const LinkConversationRow: FC<LinkConversationsDropdownRowProps> = (props) => {
  const {conversation, onOriginalConversationSelect, index, className} = props;
  const rowRef = useRef<HTMLDivElement>(null);
  const senderName = useGetSenderName(conversation);

  const renderHeaderSubject = () => conversation.subject || renderPlaceholder(conversation.messageType);
  const channelIconName = getChannelIconName(getContactSourceFromMessageType(conversation.messageType));

  return (
    <StyledDropdownItem onSelect={() => onOriginalConversationSelect(conversation.id)} index={index}>
      {({itemProps, isHighlighted}) => (
        <>
          <StyledLinkConversationsItemDiv
            ref={rowRef}
            id={itemProps.id}
            aria-selected={itemProps['aria-selected']}
            onClick={itemProps.onClick}
            onMouseDown={itemProps.onMouseDown}
            onMouseMove={itemProps.onMouseMove}
            role={itemProps.role}
            className={buildHoverParentClassName(isHighlighted, className)}
            $isHighlighted={isHighlighted}
          >
            <StyledContentDiv>
              <DropdownItemIcon label={null} iconName={channelIconName} iconSize={16} />
              <DropdownItemMultiline
                title={
                  <StyledDropdownItemHeaderTexDiv>{renderHeaderSubject()}</StyledDropdownItemHeaderTexDiv>
                }
                subtitle={<StyledDropdownItemSubHeaderTexDiv>{senderName}</StyledDropdownItemSubHeaderTexDiv>}
              />
            </StyledContentDiv>
          </StyledLinkConversationsItemDiv>

          {isHighlighted && rowRef.current && renderConversationPreview(props.panelId, rowRef, conversation)}
        </>
      )}
    </StyledDropdownItem>
  );
};

/*
 * Helpers.
 */

function renderPlaceholder(messageType: MessageTypesEnum) {
  return <em>{placeholderSubjectsByMessageType[messageType]}</em>;
}

function useGetSenderName(conversation: ConversationBaseModel) {
  const getVisualConversationSenders = makeGetVisualConversationSenders<void>(
    () => conversation,
    getUserId,
    () => conversation.senders,
  );
  const visualConversationSenders = useAppSelector(getVisualConversationSenders);
  const firstSender = visualConversationSenders ? visualConversationSenders.firstSender : undefined;
  const lastSender = visualConversationSenders ? visualConversationSenders.lastSender : undefined;

  const lastSenderName = lastSender ? formatSenderName(lastSender, undefined, undefined) : '';
  if (lastSenderName) {
    return lastSenderName;
  }

  const firstSenderName = firstSender ? formatSenderName(firstSender, undefined, undefined) : '';
  if (firstSenderName) {
    return firstSenderName;
  }

  return conversation.recipient?.displayName || ''; // Will return empty when there's no senders associated with the convo and there are no recipients. Will probably only happen for drafts
}

function renderConversationPreview(
  panelId: ViewerPanelId,
  rowRef: RefObject<HTMLDivElement>,
  conversation: ConversationBaseModel,
) {
  if (RConversation.is(conversation)) {
    return (
      <ConversationPreviewContainer
        panelId={panelId}
        conversation={conversation}
        anchor={rowRef}
        viewerConversationId={undefined}
        shouldRenderSummaryIfAvailable
      />
    );
  }
  return (
    <AsyncConversationPreviewer
      panelId={panelId}
      conversationId={conversation.id}
      anchor={rowRef}
      viewerConversationId={undefined}
      shouldRenderSummaryIfAvailable
    />
  );
}
